import React, { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Upload, ArrowLeft, X, CheckCircle2, Loader2, AlertCircle, FileText } from 'lucide-react';
import Header from '../../components/Header';
import Footer from '../../components/Footer';
import { api } from '../../lib/api';
import { bytesToHuman } from '../../lib/docs';
import { toast } from 'sonner';

const YEAR = new Date().getFullYear();
const YEARS = [YEAR, YEAR - 1, YEAR - 2, YEAR - 3, YEAR - 4];

export default function MemberUploadPage() {
  const [categories, setCategories] = useState([]);
  const [tax_year, setTaxYear] = useState(YEAR - 1);
  const [category_id, setCategoryId] = useState('');
  const [description, setDescription] = useState('');
  const [items, setItems] = useState([]); // {file, status, progress, error}
  const inputRef = useRef();
  const nav = useNavigate();

  useEffect(() => {
    api.documentCategories().then((c) => {
      setCategories(c);
      if (c.length) setCategoryId(c[0].id);
    });
  }, []);

  const onPick = (filesList) => {
    const files = Array.from(filesList || []);
    setItems((prev) => [...prev, ...files.map((f) => ({ file: f, status: 'pending', progress: 0, error: null, id: `${f.name}-${Date.now()}-${Math.random()}` }))]);
  };

  const onDrop = (e) => {
    e.preventDefault();
    onPick(e.dataTransfer.files);
  };

  const removeItem = (id) => setItems((prev) => prev.filter((i) => i.id !== id));

  const uploadAll = async () => {
    if (!category_id) return toast.error('Choose a category');
    if (items.length === 0) return toast.error('Pick at least one file');
    for (const it of items) {
      if (it.status === 'done') continue;
      const fd = new FormData();
      fd.append('file', it.file);
      fd.append('tax_year', String(tax_year));
      fd.append('category_id', category_id);
      fd.append('description', description);
      setItems((prev) => prev.map((p) => p.id === it.id ? { ...p, status: 'uploading', progress: 0, error: null } : p));
      try {
        await api.myDocumentUpload(fd, (e) => {
          const pct = e.total ? Math.round((e.loaded / e.total) * 100) : 0;
          setItems((prev) => prev.map((p) => p.id === it.id ? { ...p, progress: pct } : p));
        });
        setItems((prev) => prev.map((p) => p.id === it.id ? { ...p, status: 'done', progress: 100 } : p));
      } catch (err) {
        const msg = err?.response?.data?.detail || 'Upload failed';
        setItems((prev) => prev.map((p) => p.id === it.id ? { ...p, status: 'error', error: msg } : p));
      }
    }
    toast.success('Upload complete');
    setTimeout(() => nav('/account/documents'), 800);
  };

  return (
    <>
      <Header />
      <section className="py-10 bg-[#0c0a08] border-b border-neutral-800 text-white">
        <div className="max-w-[1100px] mx-auto px-6">
          <Link to="/account/documents" className="text-[12px] uppercase tracking-wider text-[#f5b300] inline-flex items-center gap-1 hover:underline mb-2">
            <ArrowLeft className="w-3.5 h-3.5" /> Back to documents
          </Link>
          <h1 className="text-[28px] md:text-[32px] font-extrabold text-[#f5b300]">Upload documents</h1>
          <p className="text-[13.5px] text-neutral-400 mt-1">Drag-and-drop or pick multiple files. Up to 25 MB each. PDF, JPG, PNG, DOCX, XLSX, CSV, ZIP.</p>
        </div>
      </section>

      <section className="py-10 bg-white">
        <div className="max-w-[1100px] mx-auto px-6 grid grid-cols-1 lg:grid-cols-3 gap-7">
          <div className="lg:col-span-2 space-y-6">
            <div onDragOver={(e) => e.preventDefault()} onDrop={onDrop}
                 data-testid="dropzone"
                 className="rounded-xl border-2 border-dashed border-neutral-300 hover:border-[#f5b300] transition bg-[#fbf7ee]/30 px-6 py-14 text-center cursor-pointer"
                 onClick={() => inputRef.current?.click()}>
              <Upload className="w-10 h-10 mx-auto text-[#c89200] mb-3" />
              <p className="text-[15px] font-semibold text-[#14110f]">Drop files here or click to browse</p>
              <p className="text-[12.5px] text-neutral-500 mt-1">Multiple files supported — they’ll all be tagged with the selected year and category.</p>
              <input ref={inputRef} type="file" multiple className="hidden"
                     data-testid="file-input"
                     onChange={(e) => onPick(e.target.files)} />
            </div>

            {items.length > 0 && (
              <div className="rounded-xl border border-neutral-100 bg-white p-5 shadow-[0_15px_40px_-25px_rgba(20,17,15,0.18)]">
                <h3 className="text-[15px] font-bold text-[#14110f] mb-3">Selected files ({items.length})</h3>
                <ul className="divide-y divide-neutral-100">
                  {items.map((it) => (
                    <li key={it.id} className="py-3 flex items-center gap-3">
                      <FileText className="w-5 h-5 text-[#c89200] shrink-0" />
                      <div className="flex-1 min-w-0">
                        <p className="text-[13.5px] font-semibold text-[#14110f] truncate">{it.file.name}</p>
                        <p className="text-[12px] text-neutral-500">{bytesToHuman(it.file.size)}</p>
                        {it.status === 'uploading' && (
                          <div className="h-1.5 bg-neutral-100 rounded-full mt-1.5 overflow-hidden">
                            <div className="h-full bg-[#f5b300]" style={{ width: `${it.progress}%` }} />
                          </div>
                        )}
                        {it.error && <p className="text-[12px] text-rose-600 mt-1 flex items-center gap-1"><AlertCircle className="w-3.5 h-3.5" /> {it.error}</p>}
                      </div>
                      {it.status === 'done' ? <CheckCircle2 className="w-5 h-5 text-emerald-500" />
                        : it.status === 'uploading' ? <Loader2 className="w-4 h-4 animate-spin text-neutral-400" />
                          : <button onClick={() => removeItem(it.id)} className="p-1 text-neutral-400 hover:text-rose-500"><X className="w-4 h-4" /></button>}
                    </li>
                  ))}
                </ul>
                <div className="mt-4 flex justify-end">
                  <button data-testid="upload-all-btn" onClick={uploadAll}
                          className="inline-flex items-center gap-2 bg-[#f5b300] hover:bg-[#e0a200] text-[#14110f] font-semibold px-5 py-2.5 rounded-md text-[13px]">
                    <Upload className="w-4 h-4" /> Upload all
                  </button>
                </div>
              </div>
            )}
          </div>

          <aside className="space-y-4">
            <div className="rounded-xl border border-neutral-100 bg-white p-5">
              <h3 className="text-[14px] font-bold text-[#14110f] mb-3">Tag your upload</h3>
              <label className="block text-[11.5px] uppercase tracking-wider text-neutral-500 mb-1">Tax year</label>
              <select data-testid="tax-year-select" value={tax_year} onChange={(e) => setTaxYear(parseInt(e.target.value, 10))}
                      className="w-full px-3 py-2.5 rounded-md border border-neutral-200 text-[13.5px] mb-3 focus:border-[#f5b300] outline-none">
                {YEARS.map((y) => <option key={y} value={y}>{y}</option>)}
              </select>
              <label className="block text-[11.5px] uppercase tracking-wider text-neutral-500 mb-1">Category</label>
              <select data-testid="category-select" value={category_id} onChange={(e) => setCategoryId(e.target.value)}
                      className="w-full px-3 py-2.5 rounded-md border border-neutral-200 text-[13.5px] mb-3 focus:border-[#f5b300] outline-none">
                {categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
              <label className="block text-[11.5px] uppercase tracking-wider text-neutral-500 mb-1">Notes (optional)</label>
              <textarea data-testid="description-input" value={description} onChange={(e) => setDescription(e.target.value)} rows={3}
                        placeholder="e.g. T4 from new employer"
                        className="w-full px-3 py-2.5 rounded-md border border-neutral-200 text-[13.5px] focus:border-[#f5b300] outline-none" />
            </div>
            <div className="rounded-xl border border-neutral-100 bg-[#fbf7ee] p-5">
              <h3 className="text-[14px] font-bold text-[#14110f] mb-2">Need a different category?</h3>
              <p className="text-[12.5px] text-neutral-600">Just upload as Other Supporting Documents — your tax specialist will reclassify it for you.</p>
            </div>
          </aside>
        </div>
      </section>
      <Footer />
    </>
  );
}
